import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
	"inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
	{
		variants: {
			variant: {
				default:
					"border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
				secondary:
					"border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
				accent:
					"border-transparent bg-accent text-accent-foreground hover:bg-accent/80",
				destructive:
					"border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
				success:
					"border-transparent bg-emerald-500 text-white shadow hover:bg-emerald-600",
				outline: "text-foreground",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

export interface BadgeProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof badgeVariants> {}

// forwardRef so a Badge can be a Radix `asChild` trigger. Slot hands the trigger's ref
// to its child, and a plain function component drops it — the Tooltip/HoverCard then has
// no element to anchor to and never opens.
const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
	({ className, variant, ...props }, ref) => (
		<div
			ref={ref}
			className={cn(badgeVariants({ variant }), className)}
			{...props}
		/>
	),
);
Badge.displayName = "Badge";

export { Badge, badgeVariants };
